import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Alert, Button, Card, Col, Divider, Empty, Modal, Row, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import * as React from "react";
import {
  getQuestTypeLabel,
  getRewardTypeLabel,
  QuestBonusDetail,
  QuestConditionDetail,
  QuestDetail,
  RewardType,
} from "src/hooks/affiliate";

const { Text } = Typography;

export type QuestDetailModalProps = {
  quest: QuestDetail;
};

const conditionColumns: ColumnsType<QuestConditionDetail> = [
  {
    title: "Time Window (days)",
    dataIndex: "timeWindowDay",
    key: "timeWindowDay",
    width: 160,
    align: "right",
    render: (v?: number | null) => (v == null ? "-" : v),
  },
  {
    title: "Unique User",
    dataIndex: "isUniqueUser",
    key: "isUniqueUser",
    width: 110,
    align: "center",
    render: (v?: boolean) => (v ? "Yes" : "No"),
  },
  {
    title: "Extra Config",
    dataIndex: "extraConfig",
    key: "extraConfig",
    render: (v?: Record<string, unknown> | null) =>
      v && Object.keys(v).length > 0 ? (
        <code style={{ fontSize: 12 }}>{JSON.stringify(v)}</code>
      ) : (
        <Text type="secondary">-</Text>
      ),
  },
];

const renderBonusRow = (label: string, value?: number | string | null) => (
  <Col span={8}>
    <Text type="secondary">{label}:</Text> {value == null || value === "" ? "-" : value}
  </Col>
);

export const QuestDetailModal = NiceModal.create(({ quest }: QuestDetailModalProps) => {
  const { visible, hide, remove } = useModal();
  const isBonusType = quest.rewardType === RewardType.BONUS;

  return (
    <Modal
      centered
      width={900}
      visible={visible}
      afterClose={remove}
      onCancel={hide}
      title={`รายละเอียด Quest: ${quest.questName}`}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px" }}>
          <Button size="large" onClick={hide} style={{ width: 100 }}>
            ปิด
          </Button>
        </div>
      }
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8 }}>
        <Card size="small" title="ข้อมูล Quest">
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Text type="secondary">Campaign Code:</Text> {quest.campaignCode ?? "-"}
            </Col>
            <Col span={8}>
              <Text type="secondary">Quest Type:</Text>{" "}
              <Tag>{getQuestTypeLabel(quest.questType)}</Tag>
            </Col>
            <Col span={8}>
              <Text type="secondary">Reward Type:</Text>{" "}
              <Tag color="gold">{getRewardTypeLabel(quest.rewardType)}</Tag>
            </Col>
            <Col span={8}>
              <Text type="secondary">Target Value:</Text> {quest.targetValue}
            </Col>
            <Col span={8}>
              <Text type="secondary">Reward Value:</Text>{" "}
              {quest.rewardValue != null ? quest.rewardValue : "-"}
            </Col>
            <Col span={8}>
              <Text type="secondary">Tier:</Text>{" "}
              {quest.isAllTier ? "All" : quest.tier ?? "-"}
            </Col>
            <Col span={8}>
              <Text type="secondary">Order:</Text> {quest.order ?? "-"}
            </Col>
            <Col span={8}>
              <Text type="secondary">Active:</Text>{" "}
              {quest.isActive ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Col>
            <Col span={24}>
              <Text type="secondary">ช่วงเวลา:</Text>{" "}
              {dayjs(quest.startDate).format("DD/MM/YYYY HH:mm")} →{" "}
              {dayjs(quest.endDate).format("DD/MM/YYYY HH:mm")}
            </Col>
            {quest.questDescription && (
              <Col span={24}>
                <Text type="secondary">รายละเอียด:</Text> {quest.questDescription}
              </Col>
            )}
          </Row>
        </Card>

        <Divider />

        <Card size="small" title={`Quest Conditions (${quest.conditions?.length ?? 0})`}>
          {quest.conditions?.length ? (
            <Table<QuestConditionDetail>
              rowKey={(r, i) => r.uid ?? String(i)}
              columns={conditionColumns}
              dataSource={quest.conditions}
              pagination={false}
              size="small"
            />
          ) : (
            <Empty description="ยังไม่มี condition" />
          )}
        </Card>

        <Divider />

        <Card size="small" title={`Quest Bonuses (${quest.bonuses?.length ?? 0})`}>
          {!isBonusType ? (
            <Alert
              type="info"
              showIcon
              message="Quest นี้ไม่มี bonus"
              description="เพราะ reward_type ไม่ใช่ BONUS"
            />
          ) : !quest.bonuses?.length ? (
            <Empty description="ยังไม่มี bonus" />
          ) : (
            quest.bonuses.map((b: QuestBonusDetail, idx) => (
              <Card
                key={b.uid ?? idx}
                type="inner"
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
                title={`Bonus #${idx + 1}${b.description ? ` — ${b.description}` : ""}`}
              >
                <Row gutter={[16, 4]}>
                  {renderBonusRow("Duration (hours)", b.durationInHours)}
                  {renderBonusRow("Initial Coin", b.initialCoinAmount)}
                  {renderBonusRow("Bonus Ratio", b.bonusRatio)}
                  {renderBonusRow("Turnover Ratio", b.turnoverRatio)}
                  {renderBonusRow("Maximum Bonus", b.maximumBonus)}
                  {renderBonusRow("Max per Day", b.maximumPerDay)}
                  {renderBonusRow("Convert Limit Amount", b.convertLimitAmount)}
                  {renderBonusRow("Convert Limit Ratio", b.convertLimitRatio)}
                </Row>
              </Card>
            ))
          )}
        </Card>
      </div>
    </Modal>
  );
});

export default QuestDetailModal;
